/* eslint-disable prettier/prettier */
import React from 'react'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { showToast } from './toastSlice'
import makeRequest from 'src/services/api'
import handleGeneralError from '../helpers/errorHandler'
import {
  fetchProductBasedRulesUrl,
  addProductBasedRuleUrl,
  editProductBasedRuleUrl,
  deleteProductBasedRuleUrl,
  fetchProductsUrl,
  fetchStatesUrl,
  fetchBrandsUrl,
} from 'src/services/urls'

const initialState = {
  loading: false,
  data: { hasMore: false, page: 1, totalDocs: 0, nextPage: 0 },
  products: [],
  states: [],
  docs: [],
  error: '',
  pageSize: 10,
  sortBy: 'productName',
  orderBy: 'asc',
  filterBy: '',
  activePage: 0,
  brands: [],
  filterStates: [],
}

// Fetch all product based rules
export const fetchProductBasedRules = createAsyncThunk(
  'productBasedRule/fetchProductBasedRules',
  async (options, { dispatch }) => {
    try {
      const skip = options?.skip
      const limit = options?.limit
      const productName = options?.productName
      const sortBy = options?.sortBy
      const orderBy = options?.orderBy
      const ruleType = options?.ruleType
      const states = options?.states ? options.states.map((s) => s.value) : []
      let url = fetchProductBasedRulesUrl(skip, limit, productName, sortBy, orderBy)
      if (ruleType) {
        url += `&ruleType=${ruleType}`
      }
      if(states) {
        url += `&states=${states}`
      }
      const response = await makeRequest(url)
      let docs = []
      if (response.data) {
        docs = response.data.data.docs.map((item, i) => {
          const productDetails = item.product.map((p) => ({
            productName: p.productName,
            gtin: `${p.gtin}`,
            ratio: `${p.ratio}`,
          }))
          return { ...item, srno: i + 1, productDetails: productDetails }
        })
      }

      return { docs, data: response.data.data }
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
      return { error: e?.response?.data?.error?.message }
    }
  },
)

// Add a product based rule manually - no upload widget
export const addProductBasedRule = createAsyncThunk(
  'productBasedRule/addProductBasedRule',
  async (data, { dispatch }) => {
    try {
      const body = { ...data }
      const filters = body.filters || {}
      delete body.filters
      delete body._id
      const response = await makeRequest(addProductBasedRuleUrl(), 'POST', body)
      const { displayMessage, statusMessage } = response.data

      if (response.data.statusCode === 201) {
        dispatch(showToast({ title: statusMessage, message: displayMessage, color: 'success' }))
      } else {
        dispatch(showToast({ title: 'Failed', message: displayMessage, color: 'danger' }))
      }
      dispatch(fetchProductBasedRules(filters))
      return response.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

// Edit/Update or disable a product based rule
export const editProductBasedRule = createAsyncThunk(
  'productBasedRule/editProductBasedRule',
  async (data, { dispatch }) => {
    try {
      let body = {
        ruleStartTimestamp: data.ruleStartTimestamp,
        ruleEndTimestamp: data.ruleEndTimestamp,
        points: data.points,
      }

      if (data.points === undefined) body = { isActive: data.isActive }
      const id = data._id
      const response = await makeRequest(editProductBasedRuleUrl(id), 'PUT', body)

      const { displayMessage, status } = response.data
      if (response.data.statusCode === 200) {
        dispatch(showToast({ title: status, message: displayMessage, color: 'success' }))
      }
      dispatch(fetchProductBasedRules(data?.filters))
      return response.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

// Delete a product based rule
export const deleteProductBasedRule = createAsyncThunk(
  'productBasedRule/deleteProductBasedRule',
  async (id, { dispatch }) => {
    try {
      const response = await makeRequest(deleteProductBasedRuleUrl(id), 'DELETE')
      const { displayMessage, status } = response.data

      if (response.data.statusCode === 200) {
        dispatch(showToast({ title: status, message: displayMessage, color: 'success' }))
      }

      dispatch(fetchProductBasedRules())

      return response.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

// Fetch products and states
export const fetchProductsAndStates = createAsyncThunk(
  'productBasedRule/fetchProductsAndStates',
  async (_, { dispatch }) => {
    try {
      const productsResponse = await makeRequest(fetchProductsUrl())
      const statesResponse = await makeRequest(fetchStatesUrl())

      return { products: productsResponse?.data?.data, states: statesResponse?.data?.data }
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

// Fetch brands
export const fetchBrands = createAsyncThunk(
  'productBasedRule/fetchBrands',
  async (_, { dispatch }) => {
    try {
      const brandsResponse = await makeRequest(fetchBrandsUrl())
      const brands = []
      if (brandsResponse?.data?.data) {
        brandsResponse.data.data.forEach((brand) =>
          brands.push({ value: brand._id, label: brand.brandName }),
        )
      }
      return { brands }
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

const productBasedRuleSlice = createSlice({
  name: 'productBasedRule',
  initialState,
  reducers: {
    setActivePage: (state, action) => {
      state.activePage = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    },
    setSortBy: (state, action) => {
      state.sortBy = action.payload
    },
    setOrderBy: (state, action) => {
      state.orderBy = action.payload
    },
    setFilterBy: (state, action) => {
      state.filterBy = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchProductBasedRules.pending, (state) => {
      state.loading = true
    })
    builder.addCase(fetchProductBasedRules.fulfilled, (state, action) => {
      state.loading = false
      state.error = action.payload.error
      if (!action.payload.error) {
        state.data = action.payload.data
        state.docs = action.payload.docs
      }
    })
    builder.addCase(fetchProductBasedRules.rejected, (state, action) => {
      state.loading = false
      state.data = {}
      state.docs = []
      state.error = action.error.message
    })
    builder.addCase(addProductBasedRule.fulfilled, (state, action) => {
      state.loading = false
      state.error = ''
    })
    builder.addCase(addProductBasedRule.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message
    })
    builder.addCase(editProductBasedRule.fulfilled, (state, action) => {
      state.loading = false
      state.error = ''
    })
    builder.addCase(editProductBasedRule.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message
    })
    builder.addCase(deleteProductBasedRule.fulfilled, (state, action) => {
      state.loading = false
      state.error = ''
    })
    builder.addCase(deleteProductBasedRule.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message
    })
    builder.addCase(fetchProductsAndStates.fulfilled, (state, action) => {
      state.products = action.payload?.products
      state.states = action.payload?.states
      state.filterStates = action.payload?.states.reduce((acc, curr) => {
        if (curr.state !== 'All') {
          return [...acc, { label: curr.state, value: curr._id }]
        }
        return acc
      }, [])
    })
    builder.addCase(fetchBrands.fulfilled, (state, action) => {
      state.brands = action.payload?.brands
    })
  },
})

export default productBasedRuleSlice.reducer
export const { setActivePage, setPageSize, setSortBy, setOrderBy, setFilterBy } =
  productBasedRuleSlice.actions
